/* eslint-disable react/no-unknown-property */
import React from 'react';
import { observer } from 'mobx-react-lite';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { Button, Tab, TabGroup, TabPanel } from '@pypestream/design-system';

import { NodeColumns, tableStore } from '../../../stores/table-store';
import { buildScriptFileNames } from '../../../stores/utils';
import { useProStudioCtxSelector } from '../../../xstate/app.xstate';
import { ConfigPanel } from '../src/config-panel';
import {
  getNodeTypeBadgeLetter,
  getNodeTypeBadgeModifier,
} from '../expanded-cell-view/node-type-badge';
import {
  NodeFieldTab,
  getNodeFieldGroups,
  getTypeTabLabel,
} from './node-field-groups';
import { NodePropertyField } from './node-property-field';
import { useNodeRevertPoint } from './use-node-revert-point';

import './node-property-panel.scss';

/**
 * Project Bobcat — the NODE property editor.
 *
 * Supersedes the single-cell Expanded Cell View behind the
 * master flag (`bobcat-five-stage-nav`): clicking a cell binds this panel to that
 * cell's NODE and shows all of the node's fields, grouped into three tabs. The
 * two panels never coexist — see `tableStore.shouldRenderNodePropertyPanel`.
 *
 * The layout is not declared here. Which fields appear, in which tab, in which
 * order, is derived in `node-field-groups.ts` from the store's own
 * `VISUAL_COLUMN_ORDER` and `nodeTypeDependentCells`, so the panel cannot drift
 * from the grid.
 */
export const NodePropertyPanel: React.FC<{
  onClose: () => void;
  viewOnly?: boolean;
}> = observer(({ onClose, viewOnly }) => {
  const rowId = tableStore.selectedNodeRowId;
  const row = rowId ? tableStore.getRowById(rowId) : undefined;

  const { draftFiles } = useProStudioCtxSelector((ctx) => ({
    draftFiles: ctx.draft?.files ?? [],
  }));
  const { actionNodeNames } = tableStore;

  // Command options: uploaded scripts + dynamic native functions, plus the
  // current value if it is not one of them. Read `actionNodeNames` in the
  // render body (not inside the memo) so the observer subscribes to it.
  const commandKnownFunctions = React.useMemo(
    () => new Set<string>(buildScriptFileNames(draftFiles, actionNodeNames)),
    [draftFiles, actionNodeNames]
  );
  const currentCommand = rowId
    ? String(tableStore.getValue(rowId, NodeColumns.command) ?? '')
    : '';
  const commandOptions = React.useMemo(() => {
    const set = new Set(commandKnownFunctions);
    if (currentCommand) set.add(currentCommand);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [commandKnownFunctions, currentCommand]);

  const { isDirty, revert, save, saveState } = useNodeRevertPoint(rowId);

  // Decided when the node opens, not on every type change: switching a
  // node's type on Identity must not move you to another tab.
  const opensOnType = React.useMemo(
    () => row?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.A,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowId]
  );

  // AI Action Assist takes this panel's place while open, for this node only
  // — moving to another node closes it rather than re-aiming it.
  // `selectedNodeRowId` is null for a multi-row selection or a group row, and
  // the mount gate already excludes those — but the panel can also be rendered
  // for one frame while the selection changes, so it must not assume a row.
  if (!rowId || !row) return null;

  const groups = getNodeFieldGroups(row.nodeType);
  const badge = getNodeTypeBadgeLetter(row.nodeType);
  const badgeModifier = getNodeTypeBadgeModifier(row.nodeType);

  const renderTab = (tab: NodeFieldTab) => (
    // Padding lives on this wrapper, never on `ps-tab-panel`: the panel element
    // keeps its box while hidden, so padding there leaves a gap under the tab
    // bar for every tab that is NOT showing.
    //
    // `onFocusCapture` carries over the Expanded Cell View's stale-lock guard
    // verbatim (`expanded-cell-view-panel.tsx`): this panel REPLACES that one
    // behind the flag, so without it the protection simply disappears for
    // flag holders. Clicking from an open INLINE grid edit straight into a
    // panel field would otherwise leave `editingRowId` / `draftCellValue` set
    // and `isCellBeingEdited` true — the draft never materializes and the undo
    // hotkey stays gated. `commitCellEditing` is idempotent (its
    // `rowId && columnId && raw != null` guard makes it a no-op when nothing
    // is being edited) and the capture phase runs before the focused field's
    // own `onFocus`.
    <div
      className="c-node-panel__tab-pad"
      onFocusCapture={() => tableStore.commitCellEditing()}
    >
      {groups[tab].map((columnId) => (
        <NodePropertyField
          key={columnId}
          rowId={rowId}
          columnId={columnId}
          viewOnly={viewOnly}
          commandOptions={commandOptions}
          commandKnownFunctions={commandKnownFunctions}
        />
      ))}
    </div>
  );

  return (
    <ConfigPanel
      onClose={onClose}
      footer={
        <>
          {/* One line, three states. `aria-live` so a screen reader hears the
            outcome of Save — which is otherwise conveyed only by the buttons
            going quiet. */}
          <span
            className={`c-node-panel__dirty${
              saveState === 'error' ? ' c-node-panel__dirty--error' : ''
            }`}
            test-id="ps-node-panel-dirty"
            aria-live="polite"
          >
            {(() => {
              if (saveState === 'saving') return 'Saving…';
              if (saveState === 'error')
                return "Couldn't save your changes. Try again.";
              return isDirty ? 'There are unsaved changes' : '';
            })()}
          </span>
          {/* Both take the design system's DEFAULTS: `medium` size and, for
            Save, the default `primary` variant — the purple button. They were
            `size="large"`, copied from the Supervisor panel, which is far
            wider; at 500px the large buttons crowded the footer. Save reads
            grey in a screenshot only because it is DISABLED until the node is
            dirty, which is the design's own behaviour. */}
          <Button
            test-id="ps-node-panel-cancel"
            variant="ghost"
            disabled={!isDirty || saveState === 'saving'}
            onClick={revert}
          >
            Cancel
          </Button>
          <Button
            test-id="ps-node-panel-save"
            // Stays ENABLED after a failed save, because the changes really are
            // still unsaved and retrying is the right next action. It only goes
            // quiet once the write has landed and the revert point has moved.
            disabled={!isDirty || saveState === 'saving'}
            onClick={save}
          >
            {saveState === 'saving' ? 'Saving…' : 'Save Changes'}
          </Button>
        </>
      }
    >
      <div className="c-node-panel__header" test-id="ps-node-panel-header">
        <span className="c-node-panel__node-number">
          {row.nodeNumber != null ? String(row.nodeNumber) : ''}
        </span>
        <span className="c-node-panel__node-name">{row.nodeName ?? ''}</span>
        <span
          className={`c-node-panel__badge c-node-panel__badge--${badgeModifier}`}
          test-id="ps-node-panel-badge"
        >
          {badge}
        </span>
      </div>

      {/* Keyed by node: a newly opened node lands on its default tab. An
        action node opens on its Action node tab, where Parameter Input leads
        (Edan's review, Oct 27); a decision node on Identity. */}
      <TabGroup
        key={row.id}
        test-id="ps-node-panel-tabs"
        className="c-node-panel__tabs"
      >
        <Tab slot="tab" panel="identity" active={!opensOnType}>
          Identity
        </Tab>
        {/* The panel NAME stays `type` while only the LABEL changes with the
          node type. Renaming it would unmount and remount the panel on a type
          change, throwing the user back to Identity mid-edit. */}
        <Tab slot="tab" panel="type" active={opensOnType}>
          {getTypeTabLabel(row.nodeType)}
        </Tab>
        <Tab slot="tab" panel="data">
          Data &amp; tracking
        </Tab>
        <TabPanel name="identity">{renderTab('identity')}</TabPanel>
        <TabPanel name="type">{renderTab('type')}</TabPanel>
        <TabPanel name="data">{renderTab('data')}</TabPanel>
      </TabGroup>
    </ConfigPanel>
  );
});
